/**
 * EmailPasswordForm Component
 */

import tr from '../../../../locales';
import * as actions from '../../../../redux/actions/user';
import { FieldEmail, FieldPassword } from '../../../../redux/form/utils';
import { validateEmail, validatePassword } from '../../../../utils/string';

import {
  CognitoUserPool,
  CognitoUserSession,
} from 'amazon-cognito-identity-js';
import { WithRouter, withRouter } from 'found';
import { isEqual, size, template } from 'lodash';
import * as React from 'react';
import * as BEM from 'react-bem-helper';
import { connect } from 'react-redux';
import { Form, FormProps, reduxForm, SubmissionError } from 'redux-form';

import Button from '../../../atoms/buttons/Button';
import LinkButton from '../../../atoms/buttons/LinkButton';
import FormError from '../../../atoms/feedback/FormError';
import TextField from '../../../atoms/forms/TextField';
import { IconLock, IconMail } from '../../../atoms/images/Icon';
import { Span } from '../../../atoms/text/Label';
import LoadingButton from '../../../molecules/buttons/LoadingButton';
import { ForgottenPasswordPagePath } from '../../../pages/account/ForgottenPassword';

interface IOwnStateProps {
  userPool: CognitoUserPool | undefined;
}

type AuthenticationRequest = (
  isSignUp: boolean,
  credentials: IUserCredentials,
  promise: IPromiseResolvers<any>,
) => void;

interface IOwnDispatchProps {
  requestAuthentication: AuthenticationRequest;
}

interface IOwnProps {
  action: 'login' | 'signup';
  className?: string;
  disableOppositeAction?: boolean;
  form?: string;
  initialValues?: IFormFields;
  onClickOppositeAction?: () => void;
  onForgetPassword?: () => void;
  onSubmitSuccess?: (cognitoSession: CognitoUserSession) => void;
  onSubmitFail?: (errors: any) => void;
}

type EmailPasswordFormProps =
  & IOwnProps
  & FormProps<any, IOwnProps, {}>
  & IOwnStateProps
  & IOwnDispatchProps
  & WithRouter;

interface IFormFields {
  email: string;
  password: string;
}

interface IOwnState {
  submitting: boolean;
}

const classes = new BEM('email-password-form');

class EmailPasswordForm extends React.Component<EmailPasswordFormProps, IOwnState> {
  private actionLabel: string;
  private oppositionActionLabel: string;
  private oppositionActionQuestion: string;
  private onSubmitAction: (fields: IFormFields) => Promise<{} | CognitoUserSession>;

  constructor(props: EmailPasswordFormProps) {
    super(props);

    this.state = {
      submitting: false,
    };

    if (props.action === 'login') {
      this.actionLabel = tr.login.action;
      this.oppositionActionLabel = tr.signup.action;
      this.oppositionActionQuestion = tr.signup.actionQuestion;
      this.onSubmitAction = this.onLogin;
    } else {
      this.actionLabel = tr.signup.action;
      this.oppositionActionLabel = tr.login.action;
      this.oppositionActionQuestion = tr.login.actionQuestion;
      this.onSubmitAction = this.onSignUp;
    }
  }

  shouldComponentUpdate(nextProps, nextState: IOwnState) {
    return !isEqual(nextState, this.state);
  }

  componentWillReceiveProps({ submitting }: EmailPasswordFormProps) {
    if (submitting !== this.props.submitting) {
      this.setState({ submitting: !!submitting });
    }
  }

  onSignUp = (credentials) =>
    new Promise((resolve, reject) => {
      this.props.requestAuthentication(true, credentials, { resolve, reject: this.onSignUpFailed(reject) });
    })

  onLogin = (credentials) =>
    new Promise((resolve, reject) => {
      this.props.requestAuthentication(false, credentials, { resolve, reject: this.onLoginFailed(reject) });
    })

  onSubmit = (fields: IFormFields) =>
    this.validate(fields).then(() =>
      this.onSubmitAction(fields),
    )

  renderOppositeAction() {
    const {
      action,
      disableOppositeAction,
      onClickOppositeAction,
      onForgetPassword,
      submitting,
    } = this.props;

    return (
      <div {...classes('opposite-action') }>
        {action === 'login' &&
          <LinkButton
            to={ForgottenPasswordPagePath}
            onClick={onForgetPassword}
            label="Forgot your password?"
            size="medium"
            variant="hyperlink"
            disabled={submitting}
            {...classes('forget-password-button') }
          />
        }
        {!disableOppositeAction &&
          <Span size="medium" {...classes('opposite-action__question') }>{this.oppositionActionQuestion}</Span>
        }
        {!disableOppositeAction &&
          <Button
            onClick={onClickOppositeAction}
            label={this.oppositionActionLabel}
            colorScheme="light"
            size="small"
            disabled={submitting}
          />
        }
      </div>
    );
  }

  render() {
    const {
      action,
      className,
      error,
      handleSubmit,
      submitting,
    } = this.props;

    return (
      <Form
        onSubmit={handleSubmit!(this.onSubmit)}
        {...classes('', '', className) }
      >
        {error && <FormError error={error} />}
        <FieldEmail
          component={TextField}
          colorScheme="light"
          icon={<IconMail />}
          name="email"
          placeholder={tr.inputs.email}
          disabled={submitting}
          onlyValidateIfTouched
          withoutBorder
        />
        <FieldPassword
          component={TextField}
          colorScheme="light"
          icon={<IconLock />}
          name="password"
          placeholder={tr.inputs.password}
          disabled={submitting}
          warn={action === 'signup'}
          withoutBorder
        />
        <LoadingButton
          isLoading={!!submitting}
          label={this.actionLabel}
          colorScheme="dark"
          size="medium-large"
          isSubmit
        />
        {this.renderOppositeAction()}
      </Form>
    );
  }

  private validate({ email, password }: IFormFields) {
    return new Promise((resolve, reject) => {
      const errors: any = {};

      if (!email) {
        errors.email = tr.validations.required;
      } else if (!validateEmail(email)) {
        errors.email = tr.validations.email;
      }

      if (!password) {
        errors.password = tr.validations.required;
      } else if (this.props.action === 'signup' && !validatePassword(password)) {
        errors.password = tr.validations.password;
      }

      if (size(errors)) {
        reject(new SubmissionError(errors));
      } else {
        resolve();
      }
    });
  }

  private onSignUpFailed = (reject: (reason?: any) => void) =>
    (err: Error) => {
      let message = '';
      let field: '_error' | 'email' | 'password';

      switch (err.name) {
        case 'UsernameExistsException':
          message = tr.errors.signup.alreadyExists;
          field = 'email';
          break;
        case 'InvalidPasswordException':
          message = template(tr.errors.signup.invalidPassword)({ passwordPolicy: tr.validations.password });
          field = 'password';
          break;
        default:
          message = tr.errors.signup.default;
          field = '_error';
          break;
      }

      reject(new SubmissionError({ [field]: message }));
    }

  private onLoginFailed = (reject: (reason?: any) => void) =>
    (err: Error) => {
      let message = '';
      let field: '_error' | 'email' | 'password';

      switch (err.name) {
        case 'UserNotFoundException':
          message = tr.errors.login.userNotFound;
          field = 'email';
          break;
        default:
          message = tr.errors.login.credentials;
          field = '_error';
          break;
      }

      reject(new SubmissionError({ [field]: message }));
    }
}

const ConnectedEmailPassword = connect(
  ({ authentication }: IStoreState): IOwnStateProps => ({
    userPool: authentication.userPool as CognitoUserPool,
  }),
  (dispatch): IOwnDispatchProps => ({
    requestAuthentication: (isSignUp, credentials, promise) => {
      const action = isSignUp ? actions.requestUserSignUp : actions.requestUserLogIn;
      dispatch(action({ credentials, promise }));
    },
  }),
)(EmailPasswordForm);

const ConnectedEmailPasswordReduxForm = reduxForm({
  keepDirtyOnReinitialize: true,
})(ConnectedEmailPassword);

const ConnectedEmailPasswordReduxFormWithRouter = withRouter<IOwnProps>(ConnectedEmailPasswordReduxForm);

export default ConnectedEmailPasswordReduxFormWithRouter;



// WEBPACK FOOTER //
// ./node_modules/tslint-loader??ref--0!./src/public/components/organisms/modals/AuthenticationDrawer/EmailPasswordForm.tsx